// Open Haunt's ways in from other pages (zone.json "homePortal" and "crossPromos"): the full-width animated banner
// between Home's rows (20 Oct – 10 Nov), and the two cards Events shows (the season, and Haunt night in the Plaza).
// Both are plain DOM with the zone's stylesheet; the countdowns tick once a second while they're on the page.

import { h, link } from "../../dom.ts";
import { nightTime } from "../../haunt.ts";
import { HERO_POSTER, HERO_POSTER_TALL, NIGHT_ART } from "./pack.ts";
import { countdownText, heroClock, NIGHT_AT, nightClock, phaseAt, portalOn, SEASON_END, SEASON_START, zoneNow } from "./season.ts";
import { ensureZoneStyle } from "./style.ts";

/** Calls `f` every second while `el` is in the document (it waits for the first insert). */
function tick(el: HTMLElement, f: (now: number) => void): void {
  const offset = zoneNow() - Date.now();
  let seen = false;
  const id = window.setInterval(() => {
    if (!el.isConnected) {
      if (seen) window.clearInterval(id);
      return;
    }
    seen = true;
    f(Date.now() + offset);
  }, 1000);
}

/** The Home banner, or null outside 20 Oct – 10 Nov. */
export function hauntPortal(now = zoneNow()): HTMLElement | null {
  if (!portalOn(now)) return null;
  ensureZoneStyle();
  const k = heroClock(now);
  const status = h("span", { class: `oh-status${k.live ? " live" : ""}` }, k.status);
  const digits = h("span", { class: "oh-digits" }, k.target ? countdownText(k.target, now) : "");
  const label = h("span", { class: "oh-clock-label" }, k.label ?? "");
  const el = link(
    "/haunt",
    { class: "oh-portal", "aria-label": `Open Haunt: ${k.status}. Play three haunts, light up your pumpkin.` },
    h(
      "span",
      { class: "oh-portal-art", "aria-hidden": "true" },
      h("picture", null, h("source", { media: "(max-width: 760px)", srcset: HERO_POSTER_TALL }), h("img", { src: HERO_POSTER, alt: "", loading: "lazy", decoding: "async" })),
    ),
    h("span", { class: "oh-fog", "aria-hidden": "true" }, h("i"), h("i"), h("i")),
    h(
      "span",
      { class: "oh-portal-copy" },
      h("span", { class: "oh-kicker" }, "Halloween on Banger", h("span", null, "23 Oct – 10 Nov")),
      h("strong", { class: "oh-portal-title" }, "Open Haunt"),
      h("span", { class: "oh-portal-line" }, "Play three haunts, light up your pumpkin."),
      h("span", { class: "oh-clock" }, status, label, digits),
      h("span", { class: "btn oh-btn" }, phaseAt(now) === "soon" ? "Visit Open Haunt" : "Play the haunts"),
    ),
  );
  tick(el, (t) => {
    const n = heroClock(t);
    status.textContent = n.status;
    status.classList.toggle("live", n.live);
    label.textContent = n.label ?? "";
    digits.textContent = n.target ? countdownText(n.target, t) : "";
  });
  return el;
}

/** Events' cards: the season and the night, with art and countdowns. Null once the season is over. */
export function hauntEventCards(now = zoneNow(), night: { title?: string; at?: number } | null = null): HTMLElement | null {
  if (now > SEASON_END) return null;
  ensureZoneStyle();
  const at = night?.at ?? NIGHT_AT;
  const season = h("p", { class: "oh-card-clock" }, seasonText(now));
  const n = nightClock(now, at);
  const nightClk = h("p", { class: `oh-card-clock ${n.state}` }, n.text);
  const el = h(
    "section",
    { class: "oh-cards", "aria-label": "Open Haunt" },
    link(
      "/haunt",
      { class: "oh-card" },
      h("span", { class: "oh-card-art", style: { backgroundImage: `url("${HERO_POSTER}")` } }),
      h(
        "span",
        { class: "oh-card-copy" },
        h("span", { class: "oh-eyebrow" }, "Season · 23 Oct – 10 Nov"),
        h("strong", null, "Open Haunt"),
        h("span", null, "Spooky games from creators everywhere. Visit three haunts, light up your pumpkin."),
        season,
      ),
    ),
    link(
      "/haunt#night",
      { class: "oh-card" },
      h("span", { class: "oh-card-art", style: { backgroundImage: `url("${NIGHT_ART}")` } }),
      h(
        "span",
        { class: "oh-card-copy" },
        h("span", { class: "oh-eyebrow" }, "In the Plaza"),
        h("strong", null, night?.title ?? "Haunt night in the Plaza"),
        h("span", null, `${nightTime({ at })}. We play the week's haunts live on the stage.`),
        nightClk,
      ),
    ),
  );
  tick(el, (t) => {
    season.textContent = seasonText(t);
    const k = nightClock(t, at);
    nightClk.textContent = k.text;
    nightClk.className = `oh-card-clock ${k.state}`;
  });
  return el;
}

function seasonText(now: number): string {
  if (now < SEASON_START) return `Starts in ${countdownText(SEASON_START, now)}`;
  if (now <= SEASON_END) return "Live now";
  return "";
}
